import { createBasicColorSet, KeyboardDetectionProvider, ProgressDots, } from '@spotify-internal/encore-web';
import React, { useRef, useState } from 'react';
import { CenteredPlayButton, FullControls } from '../VideoControls';
import { VideoOverlay } from '../VideoOverlay';
import { getIsControlButtonTarget, getPoster } from '../utils';
import { useVideoPlayer } from '../hooks/useVideoPlayer';
import { VideoPlayerContainer, ControlsAndOverlay, LoadingOverlay } from './ui';
import { S4XMuxVideo } from '../S4XMuxVideo/S4XMuxVideo';
import { useInForeground } from '../../../../../utils/useInForeground';
export const Player = ({ video, useDefaultThumbnail, customThumbnailType, customThumbnailTimestamp, customThumbnailImage, aspectRatio, includeSound, autoplay = false, playOnHover = false, playButtonOnLoad, videoControlColors, blurb, metadata, disabled = false, disabledUntilHover = false, toggleVolumeOnClick = false, overrideMute, onTogglePlay, onToggleMute, className, }) => {
    const ref = useRef();
    const inForeground = useInForeground(ref);
    const { showFullControls, showDisabledOverlay, showPlayButtonOnly, onMouseEnter, onMouseLeave, onFocus, onBlur, togglePlayButton, toggleVolume, onClick, onKeyboardEnter, isPlaying, isMuted, loop, } = useVideoPlayer({
        playOnHover,
        disabled,
        disabledUntilHover,
        playButtonOnLoad,
        autoplay,
        toggleVolumeOnClick,
        includeSound,
        overrideMute,
        onToggleMute,
        onTogglePlay,
        title: video.title,
        inForeground,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isHover, setIsHover] = useState(false);
    if (!video.muxData) {
        return null;
    }
    const poster = getPoster({
        customThumbnailImage,
        customThumbnailTimestamp,
        customThumbnailType,
        useDefaultThumbnail,
        playbackId: video.muxData.playbackId,
    });
    const colorSet = createBasicColorSet(videoControlColors.backgroundColor, videoControlColors.textColor);
    const showBlurbAndMetadata = (blurb || metadata) && !showDisabledOverlay && !disabled;
    return (React.createElement(KeyboardDetectionProvider, null,
        React.createElement(VideoPlayerContainer, { className: className, ref: ref, id: `video-player-container-${video.muxData.playbackId}-${video.id}`, role: "button", aspectRatio: aspectRatio, isLoading: isLoading, showFullControls: showFullControls, backgroundImage: isLoading ? poster : undefined, showDisabledOverlay: showDisabledOverlay, showBlurbAndMetadata: showBlurbAndMetadata, onMouseEnter: e => {
                e.preventDefault();
                if (!getIsControlButtonTarget(e.target)) {
                    setIsHover(true);
                    onMouseEnter();
                }
            }, onMouseLeave: e => {
                e.preventDefault();
                setIsHover(false);
                onMouseLeave();
            }, onFocus: e => {
                e.preventDefault();
                if (!e.currentTarget.contains(e.relatedTarget))
                    onFocus();
            }, onBlur: e => {
                e.preventDefault();
                if (!e.currentTarget.contains(e.relatedTarget))
                    onBlur();
            }, onClick: e => {
                if (!getIsControlButtonTarget(e.target)) {
                    onClick();
                }
            }, onKeyDown: e => {
                if (e.key === 'Enter' &&
                    !getIsControlButtonTarget(e.target)) {
                    onKeyboardEnter();
                }
            }, tabIndex: 0 },
            React.createElement(S4XMuxVideo, { muxPlaybackId: video.muxData.playbackId, "metadata-video-title": video.title, "alt-text": video.altText, style: { aspectRatio }, poster: poster, onLoadStartCapture: () => {
                    setIsLoading(true);
                }, onCanPlayCapture: () => {
                    setIsLoading(false);
                }, onEndedCapture: e => {
                    if (!autoplay && !playOnHover) {
                        e.currentTarget.currentTime = 0;
                    }
                }, loop: loop, autoPlay: autoplay && inForeground, muted: isMuted, playing: isPlaying }),
            React.createElement(ControlsAndOverlay, { tabIndex: -1 },
                React.createElement(React.Fragment, null, showPlayButtonOnly ? (React.createElement(CenteredPlayButton, { play: togglePlayButton, isHover: isHover, colorSet: colorSet })) : (React.createElement(FullControls, { vertical: aspectRatio === '9 / 16', includeSound: includeSound, colorSet: colorSet, isPlaying: isPlaying, isMuted: isMuted, togglePlay: togglePlayButton, toggleVolume: toggleVolume, disabled: disabled }))),
                (blurb || metadata) && (React.createElement(VideoOverlay, { blurb: blurb, metadata: metadata, className: "overlay" }))),
            React.createElement(LoadingOverlay, { tabIndex: -1, className: "loading-overlay" },
                React.createElement(ProgressDots, null)))));
};
